'use client';

import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';

interface Review {
  id: number;
  author: string;
  avatar: string;
  avatarAlt: string;
  date: string;
  rating: number;
  comment: string;
  country: string;
}

interface RatingCategory {
  label: string;
  score: number;
}

interface ReviewsProps {
  reviews: Review[];
  overallRating: number;
  totalReviews: number;
  categories: RatingCategory[];
}

function StarRating({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'md' }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={`${size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} ${star <= Math.round(rating) ? 'text-gray-900 fill-gray-900' : 'text-gray-300 fill-gray-300'}`}
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

export default function Reviews({ reviews, overallRating, totalReviews, categories }: ReviewsProps) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? reviews : reviews.slice(0, 6);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <svg className="w-6 h-6 text-gray-900 fill-gray-900" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
        <h2 className="text-2xl font-bold text-gray-900">{overallRating.toFixed(2)} · {totalReviews} reviews</h2>
      </div>

      {/* Rating categories */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-4 mb-10">
        {categories.map((cat) => (
          <div key={cat.label} className="flex items-center justify-between gap-3">
            <span className="text-sm text-gray-700">{cat.label}</span>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="w-24 h-1 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gray-900 rounded-full"
                  style={{ width: `${(cat.score / 5) * 100}%` }}
                />
              </div>
              <span className="text-sm font-semibold text-gray-900 w-6">{cat.score.toFixed(1)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Review cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {visible.map((review) => (
          <div key={review.id} className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="relative w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                <AppImage src={review.avatar} alt={review.avatarAlt} fill className="object-cover" />
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-900">{review.author}</div>
                <div className="text-xs text-gray-500">{review.country} · {review.date}</div>
              </div>
            </div>
            <StarRating rating={review.rating} />
            <p className="text-sm text-gray-700 leading-relaxed line-clamp-4">{review.comment}</p>
          </div>
        ))}
      </div>

      {reviews.length > 6 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="mt-8 px-6 py-3 border border-gray-900 rounded-xl text-sm font-semibold text-gray-900 hover:bg-gray-50 transition-colors"
        >
          {showAll ? 'Show fewer reviews' : `Show all ${totalReviews} reviews`}
        </button>
      )}
    </div>
  );
}
